import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import App from "./App";
import "./index.css";
import { ThemeProvider } from "@/context/ThemeProvider";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { loadConfig } from "@/utils/config";
import { updateApiBaseURL } from "@/services/api";
import { updatePublicApiBaseURL } from "@/services/settings";
import { registerSW } from "virtual:pwa-register";
import ErrorBoundary from "@/components/ErrorBoundary";

// Clean up stale SWs from the old project before registering ours
(async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        const swUrl = reg.active?.scriptURL || "";
        if (swUrl && !swUrl.includes("sw.js")) {
          await reg.unregister();
        }
      }
    }
  } catch (e) {
    // best-effort
  }
})();

registerSW({
  immediate: true,
  onNeedRefresh(reload) {
    window.dispatchEvent(
      new CustomEvent("pwa-update-available", { detail: { reload } })
    );
  },
  onOfflineReady() {},
});

// Loading component that waits for config to load
function ConfigLoader({ children }) {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig()
      .then(() => {
        // Update API base URLs after config is loaded
        updateApiBaseURL();
        updatePublicApiBaseURL();
        setConfigLoaded(true);
      })
      .catch((err) => {
        console.error("[ConfigLoader] Failed to load config:", err);
        setError(err);
        // Still render app with default config
        setConfigLoaded(true);
      });
  }, []);

  if (!configLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          />
          <p>در حال بارگذاری تنظیمات...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    console.warn("[ConfigLoader] Config load error, continuing with defaults:", error);
  }

  return children;
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ConfigLoader>
        <ThemeProvider>
          <App />
          {createPortal(
            /* document.body: above Radix Dialog (z-50) + body pointer-events lock */
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={true}
              pauseOnFocusLoss
              draggable
              theme="light"
              style={{ zIndex: 2147483646 }}
            />,
            document.body
          )}
        </ThemeProvider>
      </ConfigLoader>
    </ErrorBoundary>
  </React.StrictMode>
);
